<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import SessionHomeChannelBadge from '../components/channels/SessionHomeChannelBadge.vue'
import { shortThread } from '../composables/shortThread'
import { sessionRepo } from './AgentSessionGroups'
import type { AgentSessionSummary } from '../stores/agent/types'

// The /agent conversation header: a mono "repo · #channel" context line, the
// session title, a Running / Idle pill and a coral-outlined Stop that is
// deliberately unlike the amber Send. On phones it also carries the toggle
// that opens the Sessions drawer.
const props = defineProps<{
  activeThreadId: string | null
  // The open session's summary row; null for a brand-new session or while
  // the list hasn't loaded it yet.
  session: AgentSessionSummary | null
  running: boolean
  stopping: boolean
  isMobile: boolean
  sessionsOpen: boolean
}>()

const emit = defineEmits<{ stop: [threadId: string]; 'open-sessions': [] }>()

const repo = computed(() => (props.session ? sessionRepo(props.session) : 'superbrain'))
const title = computed(() => {
  if (!props.activeThreadId) return 'New session'
  return props.session?.name || shortThread(props.activeThreadId)
})
</script>

<template>
  <header class="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border-divider px-4 py-3 sm:px-7 sm:py-4">
    <button
      v-if="isMobile"
      type="button"
      class="inline-flex min-h-11 items-center gap-2 rounded-[10px] border border-border-control px-3 text-sm text-ink"
      data-testid="agent-sessions-toggle"
      :aria-expanded="sessionsOpen"
      aria-controls="agent-sessions-panel"
      @click="emit('open-sessions')"
    >
      <span aria-hidden="true">☰</span> Sessions
    </button>
    <div class="min-w-[200px] flex-1">
      <p v-if="activeThreadId" class="m-0 truncate font-mono text-xs text-ink-faint" data-testid="agent-context">
        <!-- Deep-links to the repo's row on the admin repos page (#1362). -->
        <RouterLink
          :to="{ name: 'adminAgentRepos', query: { repo } }"
          class="text-ink-muted no-underline hover:underline"
          data-testid="agent-context-repo-link"
          >{{ repo }}</RouterLink
        ><template v-if="session?.home_channel_id">
          · <SessionHomeChannelBadge :channel-id="session.home_channel_id"
        /></template>
      </p>
      <h1 class="m-0 mt-0.5 truncate text-[17px] font-semibold text-ink" data-testid="agent-session-title">
        {{ title }}
      </h1>
      <p v-if="!activeThreadId" class="m-0 mt-0.5 text-sm text-ink-muted">
        Drive a cloud agent session from the browser.
      </p>
    </div>
    <template v-if="activeThreadId">
      <span
        class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
        :class="running ? 'bg-factory/15 text-factory' : 'bg-surface-hover text-ink-muted'"
        data-testid="agent-status-pill"
      >
        <span v-if="running" class="size-1.5 animate-pulse rounded-full bg-factory" aria-hidden="true" />
        {{ running ? 'Running' : 'Idle' }}
      </span>
      <button
        type="button"
        class="min-h-10 rounded-[10px] border border-status-alert/60 px-3.5 text-sm font-medium text-status-alert hover:bg-status-alert/10 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="stopping"
        :aria-busy="stopping ? 'true' : undefined"
        title="Stop this session's container — its slot frees up, but the conversation stays"
        data-testid="agent-stop-session"
        @click="emit('stop', activeThreadId)"
      >
        <span aria-hidden="true">■</span> {{ stopping ? 'Stopping…' : 'Stop session' }}
      </button>
    </template>
  </header>
</template>
